/**
 * OAuth authorization-code + PKCE against Cognito Hosted UI, then token
 * exchange with Cognito Identity for temp AWS credentials.
 *
 * Flow:
 *   1. `beginLogin()` — generate PKCE verifier/challenge, redirect to Hosted UI
 *   2. Browser returns to /callback?code=... — `completeLogin()` POSTs to
 *      /oauth2/token, stashes id_token + refresh_token in sessionStorage
 *   3. `getAwsCredentials()` exchanges id_token with Cognito Identity for
 *      temp creds, caches until ~60s before expiry, refreshes on demand
 *
 * Notes:
 *   - Tokens live in sessionStorage (cleared on tab close) — a compromise
 *     between "survives page reload" and "doesn't leak to other tabs/sites".
 *     The id_token is a 1-hour JWT signed by Cognito; the refresh token is
 *     opaque but long-lived (30d) so keep an eye on the XSS posture.
 *   - No Amplify — all the auth logic is here so we can see exactly what's
 *     happening on the wire.
 */

import { getConfig } from './config';

const STORAGE = {
  pkceVerifier: 'hexlands-admin.pkce_verifier',
  pkceState: 'hexlands-admin.pkce_state',
  idToken: 'hexlands-admin.id_token',
  accessToken: 'hexlands-admin.access_token',
  refreshToken: 'hexlands-admin.refresh_token',
  idTokenExp: 'hexlands-admin.id_token_exp',
  creds: 'hexlands-admin.aws_creds', // {accessKeyId, secretAccessKey, sessionToken, expiration}
} as const;

export interface AwsCreds {
  accessKeyId: string;
  secretAccessKey: string;
  sessionToken: string;
  expiration: number; // ms epoch
}

// ── PKCE helpers ────────────────────────────────────────────────────────────

function base64UrlEncode(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let s = '';
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

async function sha256(s: string): Promise<ArrayBuffer> {
  return crypto.subtle.digest('SHA-256', new TextEncoder().encode(s));
}

function randomString(nBytes = 32): string {
  const a = new Uint8Array(nBytes);
  crypto.getRandomValues(a);
  return base64UrlEncode(a.buffer);
}

function callbackUrl(): string {
  return window.location.origin + '/callback';
}

// ── Login ───────────────────────────────────────────────────────────────────

export async function beginLogin(): Promise<void> {
  const cfg = getConfig();
  const verifier = randomString(32);
  const challenge = base64UrlEncode(await sha256(verifier));
  const state = randomString(16);

  sessionStorage.setItem(STORAGE.pkceVerifier, verifier);
  sessionStorage.setItem(STORAGE.pkceState, state);

  const url = new URL(`${cfg.userPoolDomain}/oauth2/authorize`);
  url.searchParams.set('response_type', 'code');
  url.searchParams.set('client_id', cfg.userPoolClientId);
  url.searchParams.set('redirect_uri', callbackUrl());
  url.searchParams.set('scope', 'openid email profile');
  url.searchParams.set('identity_provider', cfg.samlProviderName);
  url.searchParams.set('state', state);
  url.searchParams.set('code_challenge', challenge);
  url.searchParams.set('code_challenge_method', 'S256');

  window.location.assign(url.toString());
}

export async function completeLogin(code: string, state: string): Promise<void> {
  const cfg = getConfig();
  const savedState = sessionStorage.getItem(STORAGE.pkceState);
  const verifier = sessionStorage.getItem(STORAGE.pkceVerifier);
  if (!savedState || savedState !== state) throw new Error('OAuth state mismatch');
  if (!verifier) throw new Error('Missing PKCE verifier (open /callback in the same tab as login)');

  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: cfg.userPoolClientId,
    code,
    redirect_uri: callbackUrl(),
    code_verifier: verifier,
  });

  const res = await fetch(`${cfg.userPoolDomain}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body,
  });
  if (!res.ok) throw new Error(`Token exchange failed: ${res.status} ${await res.text()}`);

  const tok = (await res.json()) as {
    id_token: string;
    access_token: string;
    refresh_token?: string;
    expires_in: number;
  };

  sessionStorage.setItem(STORAGE.idToken, tok.id_token);
  sessionStorage.setItem(STORAGE.accessToken, tok.access_token);
  if (tok.refresh_token) sessionStorage.setItem(STORAGE.refreshToken, tok.refresh_token);
  sessionStorage.setItem(STORAGE.idTokenExp, String(Date.now() + tok.expires_in * 1000));

  sessionStorage.removeItem(STORAGE.pkceVerifier);
  sessionStorage.removeItem(STORAGE.pkceState);
}

export function isLoggedIn(): boolean {
  const exp = Number(sessionStorage.getItem(STORAGE.idTokenExp) ?? 0);
  return !!sessionStorage.getItem(STORAGE.idToken) && Date.now() < exp - 60_000;
}

export function logout(): void {
  const cfg = getConfig();
  for (const k of Object.values(STORAGE)) sessionStorage.removeItem(k);
  const url = new URL(`${cfg.userPoolDomain}/logout`);
  url.searchParams.set('client_id', cfg.userPoolClientId);
  url.searchParams.set('logout_uri', window.location.origin + '/');
  window.location.assign(url.toString());
}

// ── Refresh id_token via refresh_token grant ───────────────────────────────

async function refreshIdToken(): Promise<void> {
  const cfg = getConfig();
  const refresh = sessionStorage.getItem(STORAGE.refreshToken);
  if (!refresh) throw new Error('No refresh token — please re-login');

  const body = new URLSearchParams({
    grant_type: 'refresh_token',
    client_id: cfg.userPoolClientId,
    refresh_token: refresh,
  });
  const res = await fetch(`${cfg.userPoolDomain}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body,
  });
  if (!res.ok) throw new Error(`Refresh failed: ${res.status}`);

  const tok = (await res.json()) as {
    id_token: string;
    access_token: string;
    expires_in: number;
  };
  sessionStorage.setItem(STORAGE.idToken, tok.id_token);
  sessionStorage.setItem(STORAGE.accessToken, tok.access_token);
  sessionStorage.setItem(STORAGE.idTokenExp, String(Date.now() + tok.expires_in * 1000));
}

// ── Cognito Identity — id_token → temp AWS creds ────────────────────────────

export async function getAwsCredentials(): Promise<AwsCreds> {
  const cached = readCachedCreds();
  if (cached && cached.expiration - Date.now() > 60_000) return cached;

  // Refresh id_token first if it's close to expiry
  const idExp = Number(sessionStorage.getItem(STORAGE.idTokenExp) ?? 0);
  if (idExp - Date.now() < 120_000) {
    try {
      await refreshIdToken();
    } catch {
      // fall through — the id_token might still be valid
    }
  }

  const cfg = getConfig();
  const idToken = sessionStorage.getItem(STORAGE.idToken);
  if (!idToken) throw new Error('Not logged in');
  const loginKey = cfg.userPoolProviderUrl;

  // Step 1: GetId — identity pool returns an IdentityId for this user
  const getIdRes = await fetch(`https://cognito-identity.${cfg.region}.amazonaws.com/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-amz-json-1.1',
      'X-Amz-Target': 'AWSCognitoIdentityService.GetId',
    },
    body: JSON.stringify({
      IdentityPoolId: cfg.identityPoolId,
      Logins: { [loginKey]: idToken },
    }),
  });
  if (!getIdRes.ok) throw new Error(`GetId failed: ${getIdRes.status} ${await getIdRes.text()}`);
  const { IdentityId } = (await getIdRes.json()) as { IdentityId: string };

  // Step 2: GetCredentialsForIdentity
  const credRes = await fetch(`https://cognito-identity.${cfg.region}.amazonaws.com/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-amz-json-1.1',
      'X-Amz-Target': 'AWSCognitoIdentityService.GetCredentialsForIdentity',
    },
    body: JSON.stringify({
      IdentityId,
      Logins: { [loginKey]: idToken },
    }),
  });
  if (!credRes.ok) throw new Error(`GetCredentials failed: ${credRes.status} ${await credRes.text()}`);
  const { Credentials } = (await credRes.json()) as {
    Credentials: {
      AccessKeyId: string;
      SecretKey: string;
      SessionToken: string;
      Expiration: number; // seconds epoch
    };
  };

  const creds: AwsCreds = {
    accessKeyId: Credentials.AccessKeyId,
    secretAccessKey: Credentials.SecretKey,
    sessionToken: Credentials.SessionToken,
    expiration: Credentials.Expiration * 1000,
  };
  sessionStorage.setItem(STORAGE.creds, JSON.stringify(creds));
  return creds;
}

function readCachedCreds(): AwsCreds | null {
  const raw = sessionStorage.getItem(STORAGE.creds);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as AwsCreds;
  } catch {
    return null;
  }
}
